<template>
    <div>
        <div class="w-full pt-2 px-2" v-if="errorHandler.hasErrorMessage()">
            <p class="error pause-error--message" v-text="errorHandler.getErrorMessage()"></p>
        </div>
        <div class="pause--container">
            <pause-reason v-if="stage === 'pause-reason'" :subscriptionId="subscriptionId" v-model="pauseSurveyAnswer"/>

            <component v-else-if="stage !== 'pause-reason'"
                :is="nextStageComponent"
                :subscriptionId="subscriptionId"
                :pauseSurveyAnswer="pauseSurveyAnswer"
                :plans="plans"
                :stage="stage"
                :availableRestartDates="availableRestartDates"
                :couponDetails="couponDetails"
                :isPauseFlow="isPauseFlow"
                v-model="nextSteps"
                @deliveries-paused="handleDeliveriesPaused"
                @updateStage="updateStage"
                @subscription-updated="handleSubscriptionUpdated"
                @subscription-cancelled="handleSubscriptionCancelled"/>

            <div class="back-btn-container">
                <a @click.prevent="closePauseDeliveries">
                    <u>{{ $t('PAUSE_BACK')}}</u>
                </a>
            </div>
        </div>
    </div>
</template>

<script>
import { mapGetters } from 'vuex';
import CmsService from '@/services/cms/CmsService';
import PostcodeService from '@/services/logistics/PostcodeService';
import CouponService from '@/services/coupons/CouponService';
import ErrorResponseHandler from '@/helpers/ErrorResponseHandler';
import PauseQuestion from '@/components/partials/account/my-delivery-addresses/PauseQuestion';
import PauseReason from '@/components/partials/account/my-delivery-addresses/PauseReason';
import { ConfirmationStep, DiscountStep, LilyFreeStep, ReduceFrequencyStep, RestartStep, WeeklyEmailStep, ReminderStep, ClassicStep, FlowersMissingStep } from './pauseSteps';
import { CSPageEvents } from '@/helpers/GAHelpers';
import { PRODUCT_FAMILY_STANDARD, PRODUCT_FAMILY_LITE, PRODUCT_FAMILY_BUMPER } from '@/constants/Gifting';
import { SUBSCRIPTION_STATUS_PAUSED } from '@/constants/Subscription';

let cmsService = new CmsService();

export default {
    name: 'PauseDeliveries',
    components: { PauseQuestion, PauseReason, ConfirmationStep, DiscountStep, LilyFreeStep, ReduceFrequencyStep, RestartStep, WeeklyEmailStep, ReminderStep, ClassicStep, FlowersMissingStep },
    props: {
        subscriptionId: {
            required: true,
            type: Number,
        },
        addressId: {
            required: true,
            type: Number,
        },
    },
    data() {
        return {
            stage: 'pause-reason',
            pauseSurveyAnswerId: null,
            pauseSurveyAnswer: null,
            nextSteps: null,
            showPausedDeliveriesConfirmationModal: false,
            errorHandler: new ErrorResponseHandler(),
            plans: [],
            availableRestartDates: [],
            couponDetails: null,
            cantPause: false,
        };
    },
    mounted() {
        this.fetchPlans();
        this.fetchRestartDatesForPostcode(this.addressDetails.postcode);
        this.fetchCurrentPauseCoupon();
    },
    computed: {
        ...mapGetters({
            user: 'account/getUser',
            subscription: 'subscription/getActiveSubscription',
            isPauseFlow: 'account/getIsPauseFlow',
            activeSubscriptions: 'subscription/getActiveSubscriptions',
        }),
        nextStageComponent() {
            return `${this.stage}-step`;
        },
        subscriptionDetails() {
            return this.$store.getters['subscription/getSubscriptionById'](this.subscriptionId);
        },
        addressDetails() {
            return this.$store.getters['address/getAddressById'](this.addressId);
        },
        currentPlanFrequency() {
            return this.subscriptionDetails?.plan_slug;
        },
        pauseDiscountEnabled() {
            return !this.user?.pause_sub_coupon_redeemed;
        },
        isPaused() {
            return this.subscription.status === SUBSCRIPTION_STATUS_PAUSED;
        },
    },
    watch: {
        pauseSurveyAnswer: {
            handler() {
                if (!this.pauseSurveyAnswer) return;
                this.pauseSurveyAnswerId = this.pauseSurveyAnswer?.id;
                const firstStep = this.pauseSurveyAnswer?.nextSteps[0];
                const secondStep = this.pauseSurveyAnswer?.nextSteps[1];
                const skipFirstStep = this.skipStep(firstStep);
                if (skipFirstStep) {
                    this.stage = this.skipStep(secondStep) ? 'confirmation' : this.pauseSurveyAnswer?.nextSteps[1];
                } else {
                    this.stage = firstStep;
                }
            },
        },
        nextSteps: {
            handler() {
                if (this.nextSteps && this.nextSteps[0] !== null) {
                    const nextStep = this.skipStep(this.nextSteps[0]) ? 'confirmation' : this.nextSteps[0];
                    this.stage = nextStep;
                }
            },
        },
    },
    methods: {
        fetchData() {
            this.$store.dispatch('subscription/fetchAllSubscriptions', true);
            this.$store.dispatch('address/fetchAllAddresses', true);
            this.$store.dispatch('scheduledOrder/fetchAllDeliveries', true);
        },
        fetchPlans() {
            cmsService.getPlans().then(response => {
                const planFrequencies = response.data.data.map(item => {
                    return item.slug;
                });
                this.plans = planFrequencies;
            }).catch(error => console.error(error));
        },

        fetchRestartDatesForPostcode(postcode) {
            PostcodeService.getDeliveryDatesForPostcode(postcode, 5)
                .then(response => {
                    this.availableRestartDates = response.data.items.map(item => {
                        return {
                            label: item.human,
                            value: item.value,
                        };
                    });
                })
                .catch(error => console.error(error));
        },

        fetchCurrentPauseCoupon() {
            CouponService.getCurrentPauseCoupon().then(response => {
                this.couponDetails = response.data;
            }).catch(error => {
                this.errorHandler.setResponse(error.response.data);
                this.$emit('input', this.updatedSteps);
            });
        },

        skipStep(step) {
            const userGetsWeeklyEmail = this.user?.weekly_email;
            const userOnMonthlySubscription = this.currentPlanFrequency === 'monthly';
            const skipDiscountStep = (step === 'discount' && !this.pauseDiscountEnabled) || (step === 'discount' && !this.couponDetails);
            const skipWeeklyEmailStep = step === 'weekly-email' && userGetsWeeklyEmail;
            const skipReduceFrequency = step === 'reduce-frequency' && userOnMonthlySubscription;
            const skipLilyFree = step === 'lily-free' && (this.user?.dislikes_lilies || [PRODUCT_FAMILY_BUMPER, PRODUCT_FAMILY_LITE].includes(this.subscription?.product_family));
            const skipBumperBox = step === 'classic' && [PRODUCT_FAMILY_STANDARD, PRODUCT_FAMILY_LITE].includes(this.subscription?.product_family);
            const skipMissingFlower = step === 'flowers-missing' && this.isPauseFlow;
            return skipDiscountStep || skipWeeklyEmailStep || skipReduceFrequency || skipLilyFree || skipBumperBox || skipMissingFlower;
        },

        resetPauseSurveyAnswer() {
            return this.pauseSurveyAnswer = null;
        },
        closePauseDeliveries() {
            this.$scrollTo('.main');
            if (this.stage && this.stage === 'pause-reason') {
                return this.$router.go(-1);
            } else {
                const currentStepIndex = this.pauseSurveyAnswer.nextSteps.indexOf(this.stage);
                if (currentStepIndex === 0) {
                    this.resetPauseSurveyAnswer();
                    return this.stage = 'pause-reason';
                }
                if (currentStepIndex === 1) {
                    let prevStage = this.pauseSurveyAnswer.nextSteps[0];
                    if (this.skipStep(prevStage)) {
                        this.resetPauseSurveyAnswer();
                        return this.stage = 'pause-reason';
                    }
                    if (prevStage === 'pause-reason') this.resetPauseSurveyAnswer();
                    return this.stage = prevStage;
                }
                if (currentStepIndex > 1) {
                    let prevStages = this.pauseSurveyAnswer.nextSteps.slice(0, currentStepIndex);
                    for (let i = prevStages.length - 1; i >= 0; i--) {
                        if (this.skipStep(prevStages[i])) {
                            continue;
                        } else {
                            return this.stage = prevStages[i];
                        }
                    }
                    if (!this.stage || (this.stage === 'confirmation')) {
                        this.resetPauseSurveyAnswer();
                        return this.stage = 'pause-reason';
                    }
                    return this.stage;
                }
                this.resetPauseSurveyAnswer();
                return this.stage = 'pause-reason';
            }
        },

        handleDeliveriesPaused() {
            this.$store.commit('account/setShowFloatingBanner', true);
            this.$store.commit('account/setBannerMessage', this.$t('PAUSE_DELIVERIES_CONFIRMATION_MODAL_YOUR_DELIVERIES'));
            setTimeout(() => {
                this.$store.commit('account/setShowFloatingBanner', false);
                this.$store.commit('account/setBannerMessage', null);
            }, 5000);
            this.fetchData();
            return this.$router.go(-2);
        },

        handleSubscriptionUpdated() {
            this.$store.commit('account/setShowFloatingBanner', true);
            this.$store.commit('account/setBannerMessage', this.$t('UPDATED_DELIVERIES_CONFIRMATION_MODAL_YOUR_DELIVERIES'));
            setTimeout(() => {
                this.$store.commit('account/setShowFloatingBanner', false);
                this.$store.commit('account/setBannerMessage', null);
            }, 5000);
            this.fetchData();
            return this.$router.go(-2);
        },

        handleSubscriptionCancelled() {
            this.$store.commit('account/setShowFloatingBanner', true);
            this.$store.commit('account/setBannerMessage', 'Your subscription has been cancelled!');
            setTimeout(() => {
                this.$store.commit('account/setShowFloatingBanner', false);
                this.$store.commit('account/setBannerMessage', null);
            }, 5000);
            this.fetchData();
            return this.activeSubscriptions && this.activeSubscriptions.length === 1
                ? this.$router.push({ name: 'account.dashboard' })
                : this.$router.go(-2);
        },

        updateStage() {
            this.stage = 'pause-reason';
        },
    },
};
</script>

<style lang="scss">

.pause--container {
    padding: 15px 25px 20px;
    background-color: white;
}

.pause--heading {
    font-size: 25px;
    line-height: 30px;
    text-align: left;
}

.cancel-pause--button {
    margin: 15px 0 15px;
    cursor: pointer;
    text-align: center;
}

.pause-error--message {
    margin: 0;
    padding: 0 16px;
    font-size: 16px;
}

.restart-date--confirm {
    margin: 10px 0 0;
    background: #262323;
}

.back-btn-container {
    display: flex;
    justify-content: center;
    align-items: center;
    cursor: pointer;
    margin-top: 30px;
}

</style>
